import { Link, isRouteErrorResponse, useRouteError } from "react-router-dom";
import { t } from "../i18n";

type RouteErrorDetails = {
  readonly status: string | null;
  readonly message: string;
};

export function RouteErrorBoundary(): React.JSX.Element {
  const error = useRouteError();
  const details = getRouteErrorDetails(error);

  return (
    <main role="alert" style={styles.wrapper}>
      <section style={styles.panel}>
        <p style={styles.kicker}>{t("route_error.kicker")}</p>
        <h1 style={styles.title}>{t("route_error.title")}</h1>
        <p style={styles.description}>{t("route_error.description")}</p>
        <dl style={styles.details}>
          {details.status !== null && (
            <>
              <dt style={styles.detailLabel}>{t("route_error.status")}</dt>
              <dd style={styles.detailValue}>{details.status}</dd>
            </>
          )}
          <dt style={styles.detailLabel}>{t("route_error.message")}</dt>
          <dd style={styles.detailValue}>{details.message}</dd>
        </dl>
        <div style={styles.actions}>
          <Link to="/" style={styles.primaryAction}>
            {t("route_error.back_home")}
          </Link>
          <button
            type="button"
            style={styles.secondaryAction}
            onClick={() => window.location.reload()}
          >
            {t("route_error.reload")}
          </button>
        </div>
      </section>
    </main>
  );
}

function getRouteErrorDetails(error: unknown): RouteErrorDetails {
  if (isRouteErrorResponse(error)) {
    return {
      status: `${error.status} ${error.statusText}`.trim(),
      message: getRouteResponseMessage(error.data, error.statusText),
    };
  }
  if (error instanceof Error) {
    return { status: null, message: error.message };
  }
  if (typeof error === "string" && error.length > 0) {
    return { status: null, message: error };
  }
  return { status: null, message: t("route_error.unknown_message") };
}

function getRouteResponseMessage(data: unknown, fallback: string): string {
  if (typeof data === "string" && data.length > 0) return data;
  if (isMessageRecord(data)) return data.message;
  return fallback || t("route_error.unknown_message");
}

function isMessageRecord(
  value: unknown,
): value is { readonly message: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    "message" in value &&
    typeof value.message === "string" &&
    value.message.length > 0
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "var(--color-bg-secondary)",
  },
  panel: {
    width: "min(680px, 100%)",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    background: "var(--color-bg)",
    padding: 28,
  },
  kicker: {
    margin: "0 0 8px",
    color: "var(--color-danger)",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
  },
  title: {
    margin: "0 0 10px",
    fontSize: 24,
  },
  description: {
    margin: "0 0 20px",
    color: "var(--color-text-secondary)",
    lineHeight: 1.5,
  },
  details: {
    display: "grid",
    gridTemplateColumns: "120px minmax(0, 1fr)",
    gap: "8px 12px",
    margin: "0 0 22px",
    padding: 16,
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    background: "var(--color-bg-secondary)",
  },
  detailLabel: {
    margin: 0,
    color: "var(--color-text-secondary)",
    fontSize: 12,
    fontWeight: 700,
  },
  detailValue: {
    margin: 0,
    overflowWrap: "anywhere",
    fontFamily: "var(--font-mono)",
    fontSize: 12,
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryAction: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 36,
    padding: "0 14px",
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "white",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 700,
  },
  secondaryAction: {
    minHeight: 36,
    padding: "0 14px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    color: "var(--color-text)",
    background: "var(--color-bg)",
    fontSize: 13,
    fontWeight: 700,
  },
};
